import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Lock, Eye, EyeOff, ShieldCheck,
  ArrowLeft, ArrowRight, Loader2, Scissors, KeyRound
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import axios from 'axios';
import Toast from '../../common/components/Toast';
import { SALON_API_URL } from '../../api/config';
import BgImage from '../../Pictures/salon.jpg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const METHOD_META = {
  password: {
    label: 'Password', desc: 'Use account password', color: 'pink',
    icon: Lock,
    border: 'border-pink-500 bg-pink-50 dark:bg-pink-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-pink-300',
    btn: 'bg-pink-600 hover:bg-pink-700',
    ring: 'focus:ring-pink-500/20 focus:border-pink-500',
    pinBorder: 'border-pink-300 focus:border-pink-500 focus:ring-pink-200',
    hint: null,
  },
  email_otp: {
    label: 'Email OTP', desc: 'Login using OTP sent to email', color: 'emerald',
    icon: Mail,
    border: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-emerald-300',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
    ring: 'focus:ring-emerald-500/20 focus:border-emerald-500',
    pinBorder: 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-200',
    hint: '📧 Receive a 6-digit OTP via email to log in',
  },
  admin_pin: {
    label: 'Admin PIN', desc: '4-digit Admin PIN', color: 'indigo',
    icon: ShieldCheck,
    border: 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-indigo-300',
    btn: 'bg-indigo-600 hover:bg-indigo-700',
    ring: 'focus:ring-indigo-500/20 focus:border-indigo-500',
    pinBorder: 'border-indigo-300 focus:border-indigo-500 focus:ring-indigo-200',
    hint: '🔐 Salon Admin PIN',
  },
};

function PinBoxes({ color, pinDigits, pinRefs, onChange, onKeyDown, loading }) {
  const meta = METHOD_META[color] || METHOD_META.admin_pin;
  return (
    <div className="flex justify-center gap-3">
      {pinDigits.map((digit, i) => (
        <input
          key={i}
          ref={el => pinRefs.current[i] = el}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={e => onChange(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          disabled={loading}
          className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all
            ${meta.pinBorder} bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50`}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

function OtpBoxes({ otpDigits, otpRefs, onChange, onKeyDown, loading }) {
  return (
    <div className="flex justify-center gap-2">
      {otpDigits.map((digit, i) => (
        <input
          key={i}
          ref={el => otpRefs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={e => onChange(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          disabled={loading}
          className="w-11 h-12 text-center text-xl font-bold border-2 rounded-xl border-emerald-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 transition-all focus:outline-none"
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

export default function SalonSignIn() {
  const navigate = useNavigate();
  const { signIn: authSignIn } = useAuth();
  const pinRefs = useRef([]);
  const otpRefs = useRef([]);

  // ── Steps: 1 = email, 2 = auth method ──
  const [step, setStep]                   = useState(1);
  const [email, setEmail]                 = useState('');
  const [emailLoading, setEmailLoading]   = useState(false);
  const [methods, setMethods]             = useState({ password: true, emailOtp: true, adminPin: false });
  const [selectedMethod, setSelected]     = useState('password');
  const [password, setPassword]           = useState('');
  const [showPassword, setShowPass]       = useState(false);
  const [pinDigits, setPinDigits]         = useState(['', '', '', '']);
  const [otpDigits, setOtpDigits]         = useState(['', '', '', '', '', '']);
  const [otpSending, setOtpSending]       = useState(false);
  const [otpSent, setOtpSent]             = useState(false);
  const [otpTimer, setOtpTimer]           = useState(0);
  const [signInLoading, setSignInLoading] = useState(false);
  const [toast, setToast]                 = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  const closeToast = () => setToast(t => ({ ...t, show: false }));

  useEffect(() => {
    localStorage.setItem('businessType', 'salon');
  }, []);

  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => setOtpTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  useEffect(() => {
    if (selectedMethod === 'email_otp' && otpSent && step === 2)
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    else if (selectedMethod === 'admin_pin' && step === 2)
      setTimeout(() => pinRefs.current[0]?.focus(), 80);
  }, [selectedMethod, otpSent, step]);

  // ── Step 1 → Step 2: look up email on Salon API ────
  const handleEmailContinue = async () => {
    if (!EMAIL_RE.test(email.trim())) { showToast('Please enter a valid email address', 'error'); return; }
    setEmailLoading(true);
    try {
      const res = await axios.post(`${SALON_API_URL}/auth/check-login-methods`, {
        email: email.trim().toLowerCase(),
      });
      const m = res.data?.data?.availableMethods || { password: true, emailOtp: true };
      setMethods(m);
      if (m.password)       setSelected('password');
      else if (m.emailOtp)  setSelected('email_otp');
      else if (m.adminPin)  setSelected('admin_pin');
      setStep(2);
    } catch (err) {
      showToast(err.response?.data?.message || 'No salon account found for this email', 'error');
    } finally { setEmailLoading(false); }
  };

  // ── Send Login OTP ────────────────────────────────────────
  const handleSendLoginOtp = async () => {
    if (!EMAIL_RE.test(email.trim())) { showToast('Please enter a valid email address', 'error'); return; }
    setOtpSending(true);
    try {
      const res = await axios.post(`${SALON_API_URL}/auth/send-login-otp`, {
        email: email.trim().toLowerCase(),
      });
      if (res.data.success) {
        setOtpSent(true);
        setOtpTimer(60);
        showToast('Login OTP has been sent to your salon email', 'success');
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send OTP', 'error');
    } finally {
      setOtpSending(false);
    }
  };

  // ── Sign in ───────────────────────────────────────────────
  const handleSignIn = async (autoPin = null, autoOtp = null) => {
    let credential = '';
    if (selectedMethod === 'password') {
      credential = password;
      if (!credential) { showToast('Please enter your password', 'error'); return; }
      if (credential.length < 6) { showToast('Password must be at least 6 characters', 'error'); return; }
    } else if (selectedMethod === 'email_otp') {
      credential = autoOtp || otpDigits.join('');
      if (!credential) { showToast('Please enter the OTP', 'error'); return; }
      if (credential.length !== 6) { showToast('OTP must be 6 digits', 'error'); return; }
    } else {
      credential = autoPin || pinDigits.join('');
      if (!credential) { showToast('Please enter Admin PIN', 'error'); return; }
      if (credential.length !== 4) { showToast('PIN must be 4 digits', 'error'); return; }
    }

    setSignInLoading(true);
    try {
      const res = await axios.post(`${SALON_API_URL}/auth/smart-login`, {
        email: email.trim().toLowerCase(),
        credential,
        credentialType: selectedMethod,
      });
      if (res.data.success) {
        const { token, user, isAdminSession } = res.data;
        await authSignIn({ token, user, isAdminSession: !!isAdminSession });
        showToast('Signed in successfully!');
        navigate('/salon/dashboard', { replace: true });
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Invalid credentials', 'error');
      if (selectedMethod === 'email_otp') {
        setOtpDigits(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      } else if (selectedMethod === 'admin_pin') {
        setPinDigits(['', '', '', '']);
        setTimeout(() => pinRefs.current[0]?.focus(), 50);
      }
    } finally { setSignInLoading(false); }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...pinDigits]; next[index] = value.slice(-1); setPinDigits(next);
    if (value && index < 3) pinRefs.current[index + 1]?.focus();
    if (index === 3 && value && next.every(d => d)) handleSignIn(next.join(''));
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) pinRefs.current[index - 1]?.focus();
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otpDigits]; next[index] = value.slice(-1); setOtpDigits(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (index === 5 && value && next.every(d => d)) handleSignIn(null, next.join(''));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const availableTabs = [
    methods.password   && 'password',
    (methods.emailOtp ?? true) && 'email_otp',
    methods.adminPin   && 'admin_pin',
  ].filter(Boolean);

  const meta = METHOD_META[selectedMethod] || METHOD_META.password;

  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{
        backgroundImage: `url(${BgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/55 dark:bg-black/70 pointer-events-none z-0" />
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      {/* Header */}
      <header className="relative z-10 flex-shrink-0 flex items-center justify-between px-6 md:px-12 py-4 border-b border-white/10 bg-white/10 dark:bg-black/20 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Scissors className="w-6 h-6 text-pink-400" />
          <h2 className="text-2xl font-bold text-white">TOBO SALON</h2>
        </div>
        <button onClick={() => navigate('/signin')} className="text-white/80 text-sm font-medium hover:text-white transition-colors flex items-center gap-1">
          Switch to Restaurant Sign In
        </button>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 overflow-hidden">
        <div className="w-full max-w-[440px] max-h-full overflow-y-auto bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-700/50 p-8 md:p-10 shadow-2xl">

          {/* ══ STEP 1: Email ══ */}
          {step === 1 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate('/signin')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-pink-600 transition-colors">
                  <ArrowLeft size={16} /> Restaurant Login
                </button>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                  <Scissors size={12} /> Salon Sign In
                </div>
              </div>

              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Scissors className="w-7 h-7 text-pink-600 dark:text-pink-400" />
                </div>
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Salon Login</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enter your registered salon email</p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-600 dark:text-pink-400" />
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleEmailContinue()}
                      placeholder="salon@example.com" autoFocus autoCapitalize="none"
                      className="w-full h-12 rounded-xl pl-12 pr-4 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-600/20 focus:border-pink-600 transition-all text-sm"
                    />
                  </div>
                </div>

                <button onClick={handleEmailContinue}
                  disabled={emailLoading || !EMAIL_RE.test(email.trim())}
                  className="w-full h-12 rounded-2xl bg-pink-600 text-white font-bold text-sm shadow-lg shadow-pink-600/20 hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {emailLoading
                    ? <><Loader2 className="animate-spin" size={18} /> Checking...</>
                    : <>Continue <ArrowRight size={16} /></>}
                </button>
              </div>
            </>
          )}

          {/* ══ STEP 2: Password / OTP / Admin PIN ══ */}
          {step === 2 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <button onClick={() => { setStep(1); setPassword(''); setPinDigits(['','','','']); setOtpDigits(['','','','','','']); setOtpSent(false); }}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-pink-600 transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <div className="flex items-center gap-2 bg-pink-50 dark:bg-pink-900/30 px-3 py-1.5 rounded-full border border-pink-200 dark:border-pink-800">
                  <Scissors size={12} className="text-pink-600 dark:text-pink-400" />
                  <span className="text-xs font-bold text-pink-700 dark:text-pink-300 truncate max-w-[170px]">{email}</span>
                </div>
              </div>

              <div className="text-center mb-6">
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Salon Sign In</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose how you want to log in</p>
              </div>

              {availableTabs.length > 1 && (
                <div className={`grid gap-2 mb-5 ${availableTabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {availableTabs.map(method => {
                    const m = METHOD_META[method];
                    const MIcon = m.icon;
                    const active = selectedMethod === method;
                    return (
                      <button key={method}
                        onClick={() => { setSelected(method); setPinDigits(['','','','']); setOtpDigits(['','','','','','']); setPassword(''); }}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${active ? m.border : m.inactive}`}
                      >
                        <MIcon size={18} className={active ? `text-${m.color}-600` : 'text-gray-400'} />
                        <span className={`text-[11px] font-bold ${active ? `text-${m.color}-600` : 'text-gray-500'}`}>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {meta.hint && (
                <p className={`text-center text-xs font-medium mb-4 text-${meta.color}-600 dark:text-${meta.color}-400`}>{meta.hint}</p>
              )}

              {selectedMethod === 'password' && (
                <div className="space-y-2 mb-5">
                  <div className="flex justify-between">
                    <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Password</label>
                    <button onClick={() => navigate('/salon/forgot-password')}
                      className="text-xs font-semibold text-pink-600 hover:text-pink-700">Forgot Password?</button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-500" />
                    <input
                      type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSignIn()}
                      placeholder="Enter salon password" autoFocus
                      className="w-full h-12 rounded-xl pl-12 pr-12 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500 transition-all text-sm"
                    />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pink-600 transition-colors">
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              )}

              {selectedMethod === 'email_otp' && (
                <div className="space-y-4 mb-5">
                  {!otpSent ? (
                    <div className="text-center space-y-4 py-2">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Click below to receive a 6-digit OTP code on <span className="font-semibold text-gray-800 dark:text-gray-200">{email}</span>
                      </p>
                      <button
                        onClick={handleSendLoginOtp}
                        disabled={otpSending}
                        className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                      >
                        {otpSending ? <><Loader2 className="animate-spin" size={18} /> Sending OTP...</> : 'Send OTP to Email'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <label className="block text-sm font-semibold text-gray-900 dark:text-gray-200 text-center">
                        Enter 6-digit OTP Code
                      </label>
                      <OtpBoxes otpDigits={otpDigits} otpRefs={otpRefs} onChange={handleOtpChange} onKeyDown={handleOtpKeyDown} loading={signInLoading} />
                      <div className="flex justify-between items-center text-xs px-1">
                        <span className="text-gray-500">Code sent to email</span>
                        <button
                          type="button"
                          disabled={otpTimer > 0 || otpSending}
                          onClick={handleSendLoginOtp}
                          className="font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {otpTimer > 0 ? `Resend Code (${otpTimer}s)` : 'Resend Code'}
                        </button>
                      </div>
                      <button
                        onClick={() => handleSignIn()}
                        disabled={signInLoading || otpDigits.some(d => !d)}
                        className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                      >
                        {signInLoading ? <><Loader2 className="animate-spin" size={18} /> Verifying...</> : 'Verify & Sign In'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {selectedMethod === 'admin_pin' && (
                <div className="space-y-4 mb-5">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-200 text-center">
                    Enter 4-digit Admin PIN
                  </label>
                  <PinBoxes color="admin_pin" pinDigits={pinDigits} pinRefs={pinRefs}
                    onChange={handlePinChange} onKeyDown={handlePinKeyDown} loading={signInLoading} />
                </div>
              )}

              {selectedMethod === 'password' && (
                <button onClick={() => handleSignIn()}
                  disabled={signInLoading || password.length < 6}
                  className="w-full h-12 rounded-2xl bg-pink-600 hover:bg-pink-700 text-white font-bold text-sm shadow-lg shadow-pink-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {signInLoading ? <><Loader2 className="animate-spin" size={18} /> Signing in...</> : 'Sign In'}
                </button>
              )}

              {selectedMethod === 'admin_pin' && signInLoading && (
                <div className="flex items-center justify-center gap-2 py-3 text-slate-500">
                  <Loader2 className="animate-spin" size={18} />
                  <span className="text-sm font-medium">Verifying Admin PIN...</span>
                </div>
              )}
            </>
          )}

        </div>
      </main>
    </div>
  );
}
